import { useEffect, useState, type FunctionComponent } from 'react';
import type { IDockviewPanelHeaderProps } from 'dockview-react';
import { useT } from '@adiox/ui';
import { useMailbox } from '../mailbox/useMailbox';

/**
 * A tab that shows the title, plus a close button on extra charts only. Dockview's default tab nests a close button inside
 * every tab; here panels are shown and hidden from the Panels menu in the top bar instead, which also closes extra charts
 * for keyboard and screen-reader users (a button nested in a tab is hard for them to reach).
 */
export const DockTab: FunctionComponent<IDockviewPanelHeaderProps> = ({ api, containerApi }) => {
  const t = useT();
  const [title, setTitle] = useState(api.title);
  useEffect(() => {
    const subscription = api.onDidTitleChange((event) => setTitle(event.title));
    return () => subscription.dispose();
  }, [api]);
  // The first chart (the panel with id "chart") is permanent; every other chart can be closed individually.
  const closable = api.component === 'chart' && containerApi.getPanel('chart')?.api !== api;
  return (
    <div className="dv-default-tab">
      <div className="dv-default-tab-content">
        {title ? t(title) : title}
        {api.component === 'mailbox' && <UnreadMail />}
      </div>
      {closable && (
        <div style={{ display: 'flex', visibility: 'visible', marginLeft: 6 }}>
          <button
            type="button"
            aria-label={`Close ${title}`}
            title="Close chart"
            className="px-1 text-base leading-none opacity-70 hover:opacity-100"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              api.close();
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

/** The number of unread messages, next to the Mailbox tab's title. */
function UnreadMail() {
  const unread = useMailbox().unread;
  if (unread === 0) return null;
  return (
    <span
      className="ml-1 rounded-sm bg-accent px-1 text-[10px] font-semibold text-accent-fg"
      aria-label={`${unread} unread`}
    >
      {unread > 99 ? '99+' : unread}
    </span>
  );
}
